'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useCopy } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import type { useClawFolder } from '../../../../_hook/use-claw-folder';
import { areItemsEqual, DEFAULT_MEDIA_ROOT, getPathSegments, normalizePath } from '../../../../_utils';
import type { ClawFileItem } from '../../../../types';

interface UseMediaFolderStateOptions {
  activeClawId?: string;
  listFolder: ReturnType<typeof useClawFolder>['listFolder'];
}

export const useMediaFolderState = ({ activeClawId, listFolder }: UseMediaFolderStateOptions) => {
  const t = useTranslations();
  const { copy } = useCopy();
  const {
    rightPanelOpen,
    refreshKey,
    folderPath,
    folderItems,
    setFolderPath,
    setCurrentFolderItems,
    setGlobalLoading,
  } = useStore((state) => ({
    rightPanelOpen: state.clawRightPanelOpen,
    refreshKey: state.clawFileListRefreshKey,
    folderPath: state.clawFolderPath,
    folderItems: state.clawCurrentFolderItems,
    setFolderPath: state.setClawFolderPath,
    setCurrentFolderItems: state.setClawCurrentFolderItems,
    setGlobalLoading: state.setClawFolderLoading,
  }));

  const currentFolderItems = folderItems[folderPath] || [];

  const [loading, setLoading] = useState(false);
  const currentFolderItemsRef = useRef<ClawFileItem[]>([]);

  useEffect(() => {
    currentFolderItemsRef.current = currentFolderItems;
  }, [currentFolderItems]);

  const loadCurrentFolder = useCallback(
    async (targetFolderPath: string, options?: { silent?: boolean }) => {
      if (!activeClawId) return;
      // silent 模式仅刷数据不打断浏览（旧数据继续可见），但入口按钮 spinner 始终点亮让用户感知正在拉数据。
      if (!options?.silent) {
        setLoading(true);
      }
      setGlobalLoading(true);
      const { data, error } = await listFolder({
        folderPath: targetFolderPath,
        pageSize: 100,
      });
      if (!options?.silent) {
        setLoading(false);
      }
      setGlobalLoading(false);

      if (error || !data) {
        toast({
          type: 'error',
          message: t('claw.file.listFailed'),
        });
        return;
      }

      if (!areItemsEqual(currentFolderItemsRef.current, data)) {
        setCurrentFolderItems(targetFolderPath || DEFAULT_MEDIA_ROOT, data);
      }
    },
    [activeClawId, listFolder, setCurrentFolderItems, setGlobalLoading],
  );

  useEffect(() => {
    if (!activeClawId || !rightPanelOpen) {
      if (!rightPanelOpen) setGlobalLoading(false);
      return;
    }

    loadCurrentFolder(folderPath, {
      silent: currentFolderItemsRef.current.length > 0,
    });
  }, [activeClawId, folderPath, refreshKey, rightPanelOpen, setGlobalLoading]);

  const handleEnterFolder = useCallback(
    (item: ClawFileItem) => {
      const nextPath = normalizePath(item.path);
      if (!nextPath) return;
      reportEvent('folder_click', { folder_name: nextPath.split('/').filter(Boolean).pop() ?? nextPath });
      setFolderPath(nextPath);
    },
    [setFolderPath],
  );

  const handleNavigateToPath = useCallback(
    (path: string) => {
      setFolderPath(path || DEFAULT_MEDIA_ROOT);
    },
    [setFolderPath],
  );

  const handleRefresh = useCallback(() => {
    loadCurrentFolder(folderPath);
  }, [folderPath, loadCurrentFolder]);

  const handleCopyCurrentFolderPath = useCallback(() => {
    copy(folderPath, {
      copySuccessMessage: t('claw.file.copySuccess'),
      copyFailedMessage: t('claw.file.copyFailed'),
    });
  }, [copy, folderPath]);

  const breadcrumbItems = useMemo(() => {
    const segments = getPathSegments(folderPath);
    return segments.map((segment, index) => ({
      label: segment,
      path: segments.slice(0, index + 1).join('/'),
    }));
  }, [folderPath]);

  return {
    folderPath,
    currentFolderItems,
    loading,
    breadcrumbItems,
    handleEnterFolder,
    handleNavigateToPath,
    handleRefresh,
    handleCopyCurrentFolderPath,
    loadCurrentFolder,
  };
};
